import { AnalyticsContext, ContextType } from '@carvana/eventable-clickstream';
import React, { PureComponent, useContext } from 'react';
import VehicleImage from '@/vdp/Hero/SpinnerHero/VehicleImage';
import SpinnerContext from '~/context/vdp/SpinnerContext';
import { LoadableState } from '~/contracts/common';
import useAlertStatus from '~/hooks/vdp/selectors/useAlertStatus';
import handleChunkLoadError from '~/utilities/handleChunkLoadError';
import { logError } from '~/utilities/loggers';
import SimulateSpinnerCrash from './SimulateSpinnerCrash';

type OwnProps = {
  children: React.ReactNode;
  spinnerReady: LoadableState<boolean>;
  trackEvent: ContextType['trackEvent'];
  suppressLogs: boolean;
};

type OwnState = {
  hasError: boolean;
};

class ErrorBoundary extends PureComponent<OwnProps, OwnState> {
  constructor(props: OwnProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(err: Error, info: React.ErrorInfo) {
    const { spinnerReady, trackEvent, suppressLogs } = this.props;
    spinnerReady.setError(err);

    if (!suppressLogs) {
      trackEvent('spinnerFailed', { error: err, info });
      if (!handleChunkLoadError(err, 'Spinner Error Boundary', { info })) {
        logError(err, { location: 'Spinner Error Boundary', info });
      }
    }
  }

  render() {
    const { children } = this.props;
    const { hasError } = this.state;

    return hasError ? <VehicleImage /> : children;
  }
}

export default function SpinnerErrorBoundary({
  children
}: React.PropsWithChildren) {
  const { trackEvent } = useContext(AnalyticsContext);
  const { spinnerReady } = useContext(SpinnerContext);
  const { isSold } = useAlertStatus();

  return (
    <ErrorBoundary
      spinnerReady={spinnerReady}
      trackEvent={trackEvent}
      suppressLogs={isSold}
    >
      {children}
      <SimulateSpinnerCrash />
    </ErrorBoundary>
  );
}
